import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { NumberInput } from '../ui/number-input';
import { Checkbox } from '../ui/checkbox';
import BrandSelect from '../BrandSelect';
import ContactSelect from '../ContactSelect';
import TalentMultiSelect from './TalentMultiSelect';
import { useDeals } from '../../context/DataContext';
import { useInvoices } from '../../context/InvoicesContext';
import { useAuth } from '../../context/AuthContext';
import { CHECKLIST_ITEMS, getContentStatus, getContentStatusMeta, toggleContentStatus, computeEffectiveSplit, effectiveCommissionPercent, dealTalentIds } from '../../lib/deals';
import { fmtMoney, fmtDate } from '../../lib/format';
import { toast } from 'sonner';
import ContentStatusChecklist from '../ContentStatusChecklist';

const emptyDeal = (defaultStage = 'conversation') => ({
  title: '', talent_id: '', talent_ids: [], talent_splits: {}, brand_id: '', contact_id: null, type: '', value: 0, currency: 'GBP',
  stage: defaultStage, due_date: '', go_live_date: '', notes: '', link: '', caption: '',
  checklist: {}, commission_percent: null, po_number: '',
  invoiced_at: '', paid_in_at: '', paid_out_at: '',
});

const DealDialog = ({ open, onOpenChange, deal, defaultStage }) => {
  const { addDeal, updateDeal, removeDeal, talent, brands, stages } = useDeals();
  const { refresh: refreshInvoices } = useInvoices();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const talentEditor = user?.role === 'talent' && Boolean(user?.editing_rights);
  const readOnly = !isManager && !talentEditor;  // talent without rights (or other roles) can only view
  const isEdit = Boolean(deal?.id);
  const [form, setForm] = useState(emptyDeal(defaultStage));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const hydratingRef = React.useRef(false);

  useEffect(() => {
    hydratingRef.current = true;
    if (deal) {
      const ids = Array.isArray(deal.talent_ids) && deal.talent_ids.length
        ? deal.talent_ids
        : (deal.talent_id ? [deal.talent_id] : []);
      setForm({
        ...deal,
        talent_ids: ids,
        talent_splits: deal.talent_splits || {},
        due_date: deal.due_date || '',
        go_live_date: deal.go_live_date || '',
        link: deal.link || '',
        caption: deal.caption || '',
        checklist: deal.checklist || {},
        commission_percent: deal.commission_percent ?? null,
        invoiced_at: deal.invoiced_at || '',
        paid_in_at: deal.paid_in_at || '',
        paid_out_at: deal.paid_out_at || '',
        created_at: deal.created_at || '',
      });
    } else setForm(emptyDeal(defaultStage));
    setDirty(false);
    setSavedAt(null);
    // Release hydration guard on next tick so state-change effects don't fire from initial load
    const t = setTimeout(() => { hydratingRef.current = false; }, 0);
    return () => clearTimeout(t);
  }, [deal, defaultStage, open]);

  const markDirty = () => { if (isEdit && !hydratingRef.current) setDirty(true); };
  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); markDirty(); };
  const setCheck = (id, v) => { setForm((p) => ({ ...p, checklist: { ...p.checklist, [id]: v } })); markDirty(); };
  const setTalents = (ids, splits) => { setForm((p) => ({ ...p, talent_ids: ids, talent_splits: splits, talent_id: ids[0] || null })); markDirty(); };
  const toggleFormContentStatus = (id) => {
    const patch = toggleContentStatus(form, id);
    setForm((p) => ({ ...p, ...patch }));
    markDirty();
  };

  const selectedBrand = useMemo(() => brands.find((b) => b.id === form.brand_id), [brands, form.brand_id]);
  const selectedTalents = useMemo(
    () => (form.talent_ids || []).map((id) => talent.find((t) => t.user_id === id)).filter(Boolean),
    [talent, form.talent_ids]
  );
  const primaryTalent = selectedTalents[0] || null;
  const split = computeEffectiveSplit(form, primaryTalent);
  const usingTalentDefault = (form.commission_percent === null || form.commission_percent === undefined || form.commission_percent === '')
    && primaryTalent?.commission_percent != null
    && (form.talent_ids || []).length <= 1;
  const checklistOn = Boolean(form.checklist?.__enabled);

  // Auto-save on edit: debounced 700ms after last change
  useEffect(() => {
    if (!isEdit || !dirty || !form.title || saving || readOnly) return;
    const handle = setTimeout(async () => {
      setSaving(true);
      try {
        const result = await updateDeal(deal.id, form);
        if (result?._auto_invoice_created) {
          await refreshInvoices();
          toast.success('draft invoice created — content is live', { description: 'ready to review in invoicing' });
        }
        if (result?._marked_ready_to_invoice) {
          toast.success('marked ready to invoice', { description: 'now suggested on the talent invoicing tab' });
        } else if (
          (form.content_statuses || []).includes('content_live') &&
          !(deal.content_statuses || []).includes('content_live') &&
          (Number(form.value) || 0) <= 0
        ) {
          toast('content marked live', { description: 'add a £ rate to this deal so it can be suggested for invoicing' });
        }
        setSavedAt(new Date());
        setDirty(false);
      } finally { setSaving(false); }
    }, 700);
    return () => clearTimeout(handle);
  }, [form, dirty, isEdit]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Full manager confirms a talent's rate/stage amend — clears the red review flag.
  const clearReview = async (field) => {
    setForm((p) => ({ ...p, [field]: false }));
    try { await updateDeal(deal.id, { [field]: false }); } catch { /* keep UI optimistic */ }
  };

  const save = async () => {
    // Only used by "create deal" button — edit mode auto-saves
    if (!form.title) return;
    setSaving(true);
    try {
      await addDeal(form);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  const statusLabel = saving
    ? 'saving…'
    : dirty
      ? 'unsaved changes'
      : savedAt
        ? `saved ${savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
        : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-slate-800">{isEdit ? 'edit deal' : 'new deal'}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Title</Label>
              <button
                type="button"
                onClick={() => {
                  const brandName = selectedBrand?.name;
                  const talentNames = selectedTalents.map((t) => (t?.name || '').split(' ')[0]).filter(Boolean);
                  const talentJoined = talentNames.length ? talentNames.join(' & ') : '';
                  let t;
                  if (brandName && talentJoined) t = `${brandName} x ${talentJoined}`;
                  else if (brandName) t = `${brandName} deal`;
                  else if (talentJoined) t = `${talentJoined} deal`;
                  else t = 'New deal';
                  set('title', t);
                }}
                className="text-[11px] text-pink-700 hover:text-pink-800"
                data-testid="autoname-deal-btn"
              >
                auto-name from brand × talent
              </button>
            </div>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Nike x Rebecca September" className="mt-1" data-testid="deal-title-input" />
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Talent{(form.talent_ids || []).length > 1 ? 's' : ''}</Label>
              {(form.talent_ids || []).length > 1 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-lilac-100 text-lilac-700">multi-talent deal · set each talent's take-home</span>
              )}
            </div>
            <div className="mt-1">
              <TalentMultiSelect
                talent={talent}
                selectedIds={form.talent_ids || []}
                splits={form.talent_splits || {}}
                onChange={setTalents}
                currency={form.currency}
                talentSharePot={split.talent}
              />
            </div>
          </div>

          <div>
            <Label>Brand</Label>
            <BrandSelect value={form.brand_id} onChange={(v) => { set('brand_id', v); set('contact_id', null); }} placeholder="Search or pick brand…" className="mt-1" />
          </div>

          <div className="md:col-span-2">
            <Label>Brand contact</Label>
            <div className="mt-1">
              <ContactSelect
                brand={selectedBrand}
                value={form.contact_id || ''}
                onChange={(v) => set('contact_id', v || null)}
              />
            </div>
          </div>

          <div>
            <Label>Deliverables</Label>
            <Input
              value={form.type || ''}
              onChange={(e) => set('type', e.target.value)}
              placeholder="e.g. 2× Reels + 3 Stories, UGC pack, event appearance…"
              className="mt-1"
              data-testid="deal-deliverables-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Stage</Label>
              {isManager && form.status_review && (
                <button type="button" onClick={() => clearReview('status_review')} className="text-[10px] font-semibold text-red-600 hover:text-red-700 underline" data-testid="deal-confirm-status">talent changed — confirm</button>
              )}
            </div>
            <Select value={form.stage} onValueChange={(v) => set('stage', v)} disabled={readOnly}>
              <SelectTrigger className={`mt-1 ${isManager && form.status_review ? 'border-red-400 ring-1 ring-red-300 text-red-600' : ''}`}><SelectValue /></SelectTrigger>
              <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Value ({form.currency})</Label>
              {isManager && form.rate_review && (
                <button type="button" onClick={() => clearReview('rate_review')} className="text-[10px] font-semibold text-red-600 hover:text-red-700 underline" data-testid="deal-confirm-rate">talent changed — confirm</button>
              )}
            </div>
            <NumberInput value={form.value} onChange={(v) => set('value', v)} placeholder="0" className={`mt-1 ${isManager && form.rate_review ? 'border-red-400 ring-1 ring-red-300 text-red-600' : ''}`} />
          </div>

          <div>
            <Label>Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="GBP">GBP £</SelectItem>
                <SelectItem value="USD">USD $</SelectItem>
                <SelectItem value="EUR">EUR €</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Deal date <span className="text-slate-400 text-[10px]">— month & year, for dashboard grouping / backfill</span></Label>
            <Input
              type="month"
              value={(form.created_at || '').slice(0, 7)}
              onChange={(e) => set('created_at', e.target.value ? `${e.target.value}-01T00:00:00Z` : '')}
              className="mt-1"
              data-testid="deal-date-input"
            />
          </div>

          <div>
            <Label>PO number <span className="text-slate-400 text-[10px]">— pulls onto the invoice</span></Label>
            <Input
              value={form.po_number || ''}
              onChange={(e) => set('po_number', e.target.value)}
              placeholder="optional"
              className="mt-1"
              data-emoji-skip="true"
              data-testid="deal-po-number-input"
            />
          </div>

          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label>Content due</Label>
              <Input type="date" value={form.due_date || ''} onChange={(e) => set('due_date', e.target.value)} className="mt-1" data-testid="content-due-input" />
            </div>
            <div>
              <Label>Content live</Label>
              <Input type="date" value={form.go_live_date || ''} onChange={(e) => set('go_live_date', e.target.value)} className="mt-1" data-testid="content-live-input" />
            </div>
          </div>

          {/* Commission split */}
          <div className="md:col-span-2 border border-slate-200 rounded-xl p-4 bg-slate-50/60">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Label>Our commission (%)</Label>
                  {usingTalentDefault && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-lilac-100 text-lilac-700">talent default {primaryTalent?.commission_percent}%</span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <NumberInput
                    value={form.commission_percent ?? ''}
                    onChange={(v) => set('commission_percent', v === 0 ? null : v)}
                    placeholder={primaryTalent?.commission_percent != null ? String(primaryTalent.commission_percent) : 'e.g. 20'}
                    className="w-24"
                  />
                  <span className="text-slate-500 text-sm">%</span>
                  {form.commission_percent != null && form.commission_percent !== '' && primaryTalent?.commission_percent != null && (form.talent_ids || []).length <= 1 && (
                    <button type="button" onClick={() => set('commission_percent', null)} className="text-xs text-slate-500 hover:text-slate-800 underline">use talent default</button>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {(form.talent_ids || []).length > 1
                    ? 'single deal-level commission % applies to the total; talent share is split by the % you set per talent above.'
                    : "set a default on each talent's profile, override here per deal if needed."}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 flex-1 max-w-md">
                <div className="text-center bg-white rounded-lg border border-slate-200 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">100%</div>
                  <div className="text-sm font-semibold text-slate-800">{fmtMoney(split.full, form.currency)}</div>
                </div>
                <div className="text-center bg-white rounded-lg border border-slate-200 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">talent</div>
                  <div className="text-sm font-semibold text-slate-800">{fmtMoney(split.talent, form.currency)}</div>
                </div>
                <div className="text-center bg-pink-50 rounded-lg border border-pink-200 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-pink-700">agency</div>
                  <div className="text-sm font-semibold text-pink-800">{fmtMoney(split.agency, form.currency)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content status — multi-select; tick all that apply. The most recent tick becomes the line preview. */}
          <div className="md:col-span-2">
            <Label>Content status <span className="text-slate-400 text-[11px]">— tick everything that applies; the newest tick shows on the card/table preview</span></Label>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">preview:</span>
              {(() => {
                const meta = getContentStatusMeta(getContentStatus(form));
                return (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${meta.color}22`, color: meta.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                    {meta.label || 'not started'}
                  </span>
                );
              })()}
            </div>
            <div className="mt-2 border border-slate-200 rounded-lg p-1 max-h-52 overflow-y-auto" data-testid="content-status-dialog-checklist">
              <ContentStatusChecklist deal={form} isManager onToggle={toggleFormContentStatus} />
            </div>
          </div>

          <div className="md:col-span-2">
            <Label>Drive link</Label>
            <Input
              type="url"
              value={form.link || ''}
              onChange={(e) => set('link', e.target.value)}
              placeholder="https://drive.google.com/…  (folder holding this deal's content)"
              className="mt-1"
            />
          </div>

          <div className="md:col-span-2">
            <Label>Caption</Label>
            <Textarea
              value={form.caption || ''}
              onChange={(e) => set('caption', e.target.value)}
              rows={3}
              className="mt-1"
              placeholder="draft caption for the post (hashtags, @ tags, hook…)"
              data-testid="deal-caption-input"
            />
          </div>

          {/* Payment tracking */}
          <div className="md:col-span-2 border border-slate-200 rounded-xl p-4 bg-slate-50/60">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm">Payment</Label>
              <span className="text-[11px] text-slate-500">tick + set the date each milestone happens</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: 'invoiced_at', label: 'invoiced', color: '#38bdf8' },
                { key: 'paid_in_at', label: 'paid in (from brand)', color: '#a78bfa' },
                { key: 'paid_out_at', label: 'paid out (to talent)', color: '#059669' },
              ].map(({ key, label, color }) => {
                const on = Boolean(form[key]);
                return (
                  <div key={key} className={`bg-white rounded-lg border ${on ? 'border-slate-300' : 'border-slate-200'} p-3`}>
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={(e) => set(key, e.target.checked ? (form[key] || new Date().toISOString().slice(0,10)) : '')}
                        style={{ accentColor: color }}
                        data-testid={`pay-check-${key}`}
                      />
                      {label}
                    </label>
                    {on && (
                      <input
                        type="date"
                        value={form[key] || ''}
                        onChange={(e) => set(key, e.target.value)}
                        className="mt-2 w-full h-8 text-xs px-2 border border-slate-200 rounded-md"
                        data-testid={`pay-date-${key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={3} className="mt-1" placeholder="Deliverables, exclusivity, usage rights…" />
          </div>
        </div>

        <DialogFooter className="mt-4 flex-row justify-between gap-2 sm:justify-between items-center">
          <div className="flex items-center gap-3">
            {isEdit && (
              <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={async () => { if (window.confirm('Delete this deal?')) { await removeDeal(deal.id); onOpenChange(false); } }}>delete</Button>
            )}
            {isEdit && (
              <span
                className={`text-xs ${saving ? 'text-slate-500' : dirty ? 'text-amber-600' : 'text-slate-400'}`}
                data-testid="deal-save-status"
              >
                {statusLabel}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>{isEdit ? 'close' : 'cancel'}</Button>
            {!isEdit && (
              <Button disabled={saving || !form.title} onClick={save} className="bg-pink-500 hover:bg-pink-600 text-white font-semibold">{saving ? 'saving…' : 'create deal'}</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DealDialog;
